import React, { useMemo } from "react";
import _ from "lodash";
import { encodeSvg, getElementDimensions, getOffsetsX, hexToRgb, WATERMARK_STRIP_WIDTH } from "helpers/watermarking";
import { IWatermarking } from "reducers/watermarking";
import { BrandRecord } from "records/brand";

const MINIMAL_WATERMARK_GAP = 50;
const OFFSETS_Y = [["62%"], ["50%", "83%"], ["38%", "64%", "90%"]];
const SCHEMA_COMPANY = "company";

interface IProps {
    onIsFetchingChange?: (isFetching: boolean) => void;
    brand?: BrandRecord;
    watermarking: IWatermarking;
    company?: any;
    previewMode?: boolean;
}

export const useWatermarking = ({ watermarking, brand, company, previewMode = false }: IProps) => {
    const { active, text, textColor, textOpacity, backgroundColor, backgroundOpacity, dataAttributes } =
        watermarking || {};
    const useWatermarks = useMemo(() => brand?.hasPreference("add_watermarking") && active, [brand, active]);
    const elementsToDisplay = useMemo(
        () => _.filter([text, ..._.map(dataAttributes, ({ key }) => company?.[key])]),
        [text, dataAttributes, company]
    );

    const offsetsY = useMemo(() => OFFSETS_Y[elementsToDisplay.length - 1], [elementsToDisplay.length]);
    return useMemo(() => {
        if (!previewMode && (!useWatermarks || !_.size(elementsToDisplay))) {
            return null;
        }
        const classes = getWatermarkStyles({
            textColor,
            textOpacity,
            backgroundColor,
            backgroundOpacity,
        });

        // render watermark and get it's dimensions
        const [watermarkWidth] = getElementDimensions(
            <>
                {elementsToDisplay.map((element, index) => (
                    <span key={index} style={{ ...classes.title, ...(index > 0 ? classes.description : {}) }}>
                        {element}
                    </span>
                ))}
            </>
        );

        const offsetsX = getOffsetsX(watermarkWidth, MINIMAL_WATERMARK_GAP);
        // renders a strip with a couple of watermarks depending on the length of text in the watermark
        const svg = (
            <svg
                viewBox={`0 0 ${WATERMARK_STRIP_WIDTH} 83`}
                xmlns="http://www.w3.org/2000/svg"
                width="1200"
                height="83"
            >
                <rect style={classes.background} />
                {_.map(offsetsX, (offsetX, i) => (
                    <text y="20" x={offsetX} style={classes.title} key={i}>
                        {elementsToDisplay.map((element, index) => (
                            <tspan
                                x={offsetX}
                                key={index}
                                textAnchor="middle"
                                y={offsetsY[index]}
                                style={index > 0 ? classes.description : {}}
                            >
                                {element}
                            </tspan>
                        ))}
                    </text>
                ))}
            </svg>
        );
        return encodeSvg(svg);
    }, [
        previewMode,
        useWatermarks,
        elementsToDisplay,
        textColor,
        textOpacity,
        backgroundColor,
        backgroundOpacity,
        offsetsY,
    ]);
};

const getWatermarkStyles = ({ textColor, textOpacity, backgroundColor, backgroundOpacity }) => ({
    background: {
        fill: backgroundColor
            ? `rgb(${hexToRgb(backgroundColor).join(",")}, ${backgroundOpacity / 100})`
            : "rgb(0,0,0,0)",
        width: "100%",
        height: "100%",
    },
    title: {
        fontSize: "24px",
        fill: textColor ? `rgb(${hexToRgb(textColor).join(",")}, ${textOpacity / 100})` : "rgb(0,0,0,0)",
        translate: "transformX(-50%)",
    },
    description: {
        fontSize: "18px",
    },
});
